import { useEffect, useId, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Select } from '@/components/ui/Select'
import { useClient, useClientList } from '@/lib/clients-api'
import { toNumber } from '@/lib/decimal'
import { formatContractHours } from '@/lib/format'
import type {
  Opportunity,
  OpportunityInput,
  OpportunityRevenueModel,
  OpportunityStage,
  OpportunityStatus,
} from '@/lib/planning-types'

const NO_CLIENT = 'none'
const NO_PROJECT = 'none'

const STAGES: { value: OpportunityStage; label: string }[] = [
  { value: 'LIKELY', label: 'Waarschijnlijk (75%)' },
  { value: 'POSSIBLE', label: 'Mogelijk (50%)' },
  { value: 'SPECULATIVE', label: 'Speculatief (25%)' },
]

const MODELS: { value: OpportunityRevenueModel; label: string }[] = [
  { value: 'HOURLY', label: 'Uurtarief' },
  { value: 'FIXED', label: 'Vaste prijs' },
]

interface OpportunityModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Null = een nieuwe kans. */
  opportunity: Opportunity | null
  onSave: (input: OpportunityInput, status: OpportunityStatus) => void
  onDelete: () => void
  saving?: boolean
}

/**
 * Alles in dit venster is een concept tot je opslaat, ook de status. Eerder
 * muteerden de statusknoppen meteen en sloten ze het venster, waardoor een naam
 * of notitie die je net had getypt verdween.
 */
export function OpportunityModal({ open, onOpenChange, opportunity, onSave, onDelete, saving }: OpportunityModalProps) {
  const [name, setName] = useState('')
  const [clientId, setClientId] = useState(NO_CLIENT)
  const [projectId, setProjectId] = useState(NO_PROJECT)
  const [stage, setStage] = useState<OpportunityStage>('POSSIBLE')
  const [status, setStatus] = useState<OpportunityStatus>('ACTIVE')
  const [revenueModel, setRevenueModel] = useState<OpportunityRevenueModel>('HOURLY')
  const [rate, setRate] = useState('')
  const [totalHours, setTotalHours] = useState('')
  const [notes, setNotes] = useState('')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(opportunity?.name ?? '')
    setClientId(opportunity?.client?.id ?? NO_CLIENT)
    setProjectId(opportunity?.project?.id ?? NO_PROJECT)
    setStage(opportunity?.stage ?? 'POSSIBLE')
    setStatus(opportunity?.status ?? 'ACTIVE')
    setRevenueModel(opportunity?.revenueModel ?? 'HOURLY')
    setRate(opportunity?.hourlyRateOverride ?? '')
    // Zonder de nullen achter de komma: je typt "160", niet "160,00".
    setTotalHours(opportunity?.totalHours ? formatContractHours(toNumber(opportunity.totalHours)) : '')
    setNotes(opportunity?.notes ?? '')
    setConfirmingDelete(false)
  }, [open, opportunity])

  const clients = useClientList(undefined, open)
  const client = useClient(clientId !== NO_CLIENT ? clientId : '')
  const projects = clientId !== NO_CLIENT && client.data ? client.data.projects : []

  const clientOptions = [
    { value: NO_CLIENT, label: 'Prospect (nog geen klant)' },
    ...(clients.data ?? []).map((row) => ({
      value: row.id,
      label: row.internal ? `${row.companyName} (intern)` : row.companyName,
    })),
  ]

  const projectOptions = [
    { value: NO_PROJECT, label: 'Nog geen project' },
    ...projects.map((project) => ({ value: project.id, label: project.name })),
  ]

  const submit = () =>
    onSave(
      {
        name,
        clientId: clientId === NO_CLIENT ? null : clientId,
        projectId: projectId === NO_PROJECT ? null : projectId,
        stage,
        revenueModel,
        hourlyRateOverride: rate.trim(),
        totalHours: totalHours.trim(),
        notes,
      },
      status,
    )

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={opportunity ? 'Kans bewerken' : 'Nieuwe kans'}
      description={
        opportunity ? undefined : 'Werk dat je verwacht. Een klant is niet verplicht: een prospect bestaat nog niet in het systeem.'
      }
      footer={
        <>
          {opportunity ? (
            confirmingDelete ? (
              <span className="mr-auto flex items-center gap-2 text-xs text-ink-soft">
                Definitief weg, inclusief de uren.
                <button type="button" className="btn btn-ghost h-8 px-2 text-xs text-status-overdue" onClick={onDelete}>
                  Verwijderen
                </button>
                <button type="button" className="btn btn-ghost h-8 px-2 text-xs" onClick={() => setConfirmingDelete(false)}>
                  Terug
                </button>
              </span>
            ) : (
              <button type="button" className="btn btn-ghost mr-auto text-ink-faint" onClick={() => setConfirmingDelete(true)}>
                Verwijderen
              </button>
            )
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={saving || name.trim() === ''}>
            Opslaan
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Naam">
          {(id) => (
            <input
              aria-labelledby={id}
              className="field w-full"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Waar gaat het werk over"
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Klant">
            {(id) => (
              <Select
                ariaLabelledby={id}
                value={clientId}
                onValueChange={(value) => {
                  setClientId(value)
                  setProjectId(NO_PROJECT)
                }}
                options={clientOptions}
                className="w-full"
              />
            )}
          </Field>
          <Field label="Project">
            {(id) => (
              <Select
                ariaLabelledby={id}
                value={projectId}
                onValueChange={setProjectId}
                options={projectOptions}
                disabled={clientId === NO_CLIENT}
                className="w-full"
              />
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Status">
            {() => (
              <SegmentedControl<OpportunityStatus>
                label="Status"
                value={status}
                onChange={setStatus}
                segments={[
                  { value: 'ACTIVE', label: 'Actief' },
                  { value: 'WON', label: 'Gewonnen' },
                  { value: 'LOST', label: 'Verloren' },
                ]}
              />
            )}
          </Field>
          {status === 'ACTIVE' ? (
            <Field label="Kans">
              {(id) => (
                <Select
                  ariaLabelledby={id}
                  value={stage}
                  onValueChange={(value) => setStage(value as OpportunityStage)}
                  options={STAGES}
                  className="w-full"
                />
              )}
            </Field>
          ) : (
            <p className="self-end text-xs text-ink-faint">
              {status === 'WON'
                ? 'Gewonnen werk telt voor 100% mee.'
                : 'Verloren werk verdwijnt uit het overzicht; je kunt het onderaan de kansenlijst terughalen.'}
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Opbrengst">
            {(id) => (
              <Select
                ariaLabelledby={id}
                value={revenueModel}
                onValueChange={(value) => setRevenueModel(value as OpportunityRevenueModel)}
                options={MODELS}
                className="w-full"
              />
            )}
          </Field>
          {revenueModel === 'HOURLY' ? (
            <Field label="Uurtarief" hint="Leeg = het tarief van de klant">
              {(id) => (
                <input
                  aria-labelledby={id}
                  className="field w-full text-right"
                  inputMode="decimal"
                  value={rate}
                  onChange={(event) => setRate(event.target.value)}
                  placeholder="Klanttarief"
                />
              )}
            </Field>
          ) : (
            <p className="self-end text-xs text-ink-faint">
              De bedragen per maand vul je in de kansenlijst in; de uren bepalen de vooruitblik.
            </p>
          )}
        </div>

        <Field label="Omvang" hint="Uren die je kent voor je weet wie ze maakt; leeg = geen omvang">
          {(id) => (
            <input
              aria-labelledby={id}
              className="field w-full text-right sm:w-40"
              inputMode="decimal"
              value={totalHours}
              onChange={(event) => setTotalHours(event.target.value)}
              placeholder="Onbekend"
            />
          )}
        </Field>

        <Field label="Notitie">
          {(id) => (
            <textarea
              aria-labelledby={id}
              className="field min-h-[72px] w-full"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Wat je wilt onthouden bij deze kans"
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}

/**
 * Geen `<label>` om de inhoud heen: een Radix-select is een knop, en dan leest
 * een schermlezer het label plus de huidige waarde als een naam. De koptekst
 * krijgt een id en het veld verwijst ernaar.
 */
function Field({ label, hint, children }: { label: string; hint?: string; children: (id: string) => React.ReactNode }) {
  const id = useId()

  return (
    <div>
      <span className="label-caps" id={id}>
        {label}
      </span>
      <div className="mt-1.5">{children(id)}</div>
      {hint ? <span className="mt-1 block text-xs text-ink-faint">{hint}</span> : null}
    </div>
  )
}
